
import React, { useState, useEffect, useMemo } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
} from "firebase/firestore";

export default function AdminAddFinalScore() {
  const [matches, setMatches] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("today");
  const [editingMatch, setEditingMatch] = useState(null);
  const [newScore, setNewScore] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const showMsg = (msg) => {
    setMessage(msg);
    setTimeout(() => setMessage(""), 5000);
  };

  // Pata tarehe bila kubadilisha siku ya YYYY-MM-DD.
  function parseMatchDate(value) {
    if (!value) return null;

    const text = String(value).trim();
    const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (match) {
      return new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3])
      );
    }

    const parsed = new Date(text);
    if (Number.isNaN(parsed.getTime())) return null;

    return new Date(
      parsed.getFullYear(),
      parsed.getMonth(),
      parsed.getDate()
    );
  }

  function getMatchDate(match) {
    return match.date || match.matchDate || "";
  }

  function getMatchTime(match) {
    return match.time || match.matchTime || "";
  }

  function getTeamA(match) {
    return match.A || match.teamA || "";
  }

  function getTeamB(match) {
    return match.B || match.teamB || "";
  }

  // 1. LOAD MATCHES
  useEffect(() => {
    let active = true;

    async function loadMatches() {
      try {
        const snapshot = await getDocs(collection(db, "matches"));

        const list = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));

        list.sort((a, b) => {
          const dateA = parseMatchDate(getMatchDate(a));
          const dateB = parseMatchDate(getMatchDate(b));

          const valueA = dateA ? dateA.getTime() : 0;
          const valueB = dateB ? dateB.getTime() : 0;

          if (valueA !== valueB) return valueB - valueA;

          return getMatchTime(b).localeCompare(getMatchTime(a));
        });

        if (active) setMatches(list);
      } catch (error) {
        console.error("Error loading matches:", error);
        if (active) showMsg("❌ Imeshindikana kusoma mechi kutoka Firebase.");
      } finally {
        if (active) setLoading(false);
      }
    }

    loadMatches();

    return () => {
      active = false;
    };
  }, []);

  // 2. LIVE SEARCH + DATE FILTER
  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    // Tarehe ya sasa kwa Tanzania
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Africa/Dar_es_Salaam",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date());

    const part = (name) =>
      parts.find((item) => item.type === name)?.value;

    const today = new Date(
      Number(part("year")),
      Number(part("month")) - 1,
      Number(part("day"))
    );

    const startOfWeek = new Date(today);
    const dayFromMonday = (today.getDay() + 6) % 7;
    startOfWeek.setDate(today.getDate() - dayFromMonday);

    const startOfNextWeek = new Date(startOfWeek);
    startOfNextWeek.setDate(startOfWeek.getDate() + 7);

    const startOfLastWeek = new Date(startOfWeek);
    startOfLastWeek.setDate(startOfLastWeek.getDate() - 7);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    return matches.filter((match) => {
      const teamA = getTeamA(match);
      const teamB = getTeamB(match);
      const dateValue = getMatchDate(match);
      const timeValue = getMatchTime(match);

      const searchableText = [
        teamA,
        teamB,
        dateValue,
        timeValue,
        match.finalScore,
        match.league,
        match.country,
      ]
        .map((value) => String(value ?? ""))
        .join(" ")
        .toLowerCase();

      // Search huanza kufanya kazi unapoandika.
      if (term && !searchableText.includes(term)) {
        return false;
      }

      // All Matches haichuji kwa tarehe.
      if (dateFilter === "all") return true;

      const matchDate = parseMatchDate(dateValue);
      if (!matchDate) return false;

      if (dateFilter === "today") {
        return matchDate.getTime() === today.getTime();
      }

      if (dateFilter === "yesterday") {
        return matchDate.getTime() === yesterday.getTime();
      }

      if (dateFilter === "thisWeek") {
        return (
          matchDate >= startOfWeek &&
          matchDate < startOfNextWeek
        );
      }

      if (dateFilter === "lastWeek") {
        return (
          matchDate >= startOfLastWeek &&
          matchDate < startOfWeek
        );
      }

      return true;
    });
  }, [matches, searchTerm, dateFilter]);

  // 3. UPDATE BETS THAT MATCH THIS FIXTURE
  async function updateMatchingBets(match, score) {
    const teamA = getTeamA(match);
    const teamB = getTeamB(match);
    const date = getMatchDate(match);
    const time = getMatchTime(match);

    const foundBets = new Map();

    // Schema ya bets inayotumia A, B, date, time.
    try {
      const q1 = query(
        collection(db, "userBets"),
        where("A", "==", teamA),
        where("B", "==", teamB),
        where("date", "==", date),
        where("time", "==", time)
      );

      const result1 = await getDocs(q1);
      result1.docs.forEach((item) => foundBets.set(item.id, item));
    } catch (error) {
      console.warn("Bets schema A/B/date/time haikupatikana:", error);
    }

    // Schema ya zamani inayotumia teamA, teamB, matchDate, matchTime.
    try {
      const q2 = query(
        collection(db, "userBets"),
        where("teamA", "==", teamA),
        where("teamB", "==", teamB),
        where("matchDate", "==", date),
        where("matchTime", "==", time)
      );

      const result2 = await getDocs(q2);
      result2.docs.forEach((item) => foundBets.set(item.id, item));
    } catch (error) {
      console.warn(
        "Bets schema teamA/teamB/matchDate/matchTime haikupatikana:",
        error
      );
    }

    // Hifadhi score kwenye bets zilizolingana.
    for (const bet of foundBets.values()) {
      await updateDoc(doc(db, "userBets", bet.id), {
        finalScore: score,
      });
    }

    return foundBets.size;
  }

  // 4. SAVE FINAL SCORE
  async function saveFinalScore(match) {
    const score = newScore.trim();

    if (!score) {
      showMsg("⚠️ Tafadhali andika final score, mfano 2-1.");
      return;
    }

    if (saving) return;

    setSaving(true);

    try {
      // Sasisha mechi yenyewe.
      await updateDoc(doc(db, "matches", match.id), {
        finalScore: score,
      });

      // Sasisha bets zinazolingana.
      let updatedBets = 0;

      try {
        updatedBets = await updateMatchingBets(match, score);
      } catch (error) {
        console.error("Error updating bets:", error);

        setMatches((previous) =>
          previous.map((item) =>
            item.id === match.id
              ? { ...item, finalScore: score }
              : item
          )
        );

        setEditingMatch(null);
        setNewScore("");

        showMsg(
          "⚠️ Score imehifadhiwa kwenye matches, lakini kusasisha userBets kumeshindikana. Kagua console."
        );
        return;
      }

      setMatches((previous) =>
        previous.map((item) =>
          item.id === match.id
            ? { ...item, finalScore: score }
            : item
        )
      );

      setEditingMatch(null);
      setNewScore("");

      showMsg(
        `✅ Score ${score} imehifadhiwa. Bets zilizosasishwa: ${updatedBets}.`
      );
    } catch (error) {
      console.error("Error saving final score:", error);
      showMsg("❌ Hitilafu kuhifadhi score. Angalia Firebase na console.");
    } finally {
      setSaving(false);
    }
  }

  function cancelEditing() {
    setEditingMatch(null);
    setNewScore("");
  }

  return (
    <div className="admin-final-score">
      <style>{`
        .admin-final-score {
          box-sizing: border-box;
          width: 100%;
          max-width: 100%;
          min-height: 100%;
          padding: 20px;
          background: #111827;
          color: #f4f6fb;
          font-family: Arial, sans-serif;
        }

        .admin-final-score *,
        .admin-final-score *::before,
        .admin-final-score *::after {
          box-sizing: border-box;
        }

        .admin-final-score h2 {
          margin: 0 0 18px;
          color: #fff;
          font-size: 22px;
          line-height: 1.4;
        }

        .afs-search-area {
          position: relative;
          width: 100%;
          margin-bottom: 12px;
        }

        .afs-search-input {
          display: block;
          width: 100%;
          min-height: 46px;
          padding: 12px 42px 12px 14px;
          border: 1px solid #374151;
          border-radius: 9px;
          outline: none;
          background: #1f2937;
          color: #fff;
          font-size: 16px;
        }

        .afs-search-input::placeholder {
          color: #9ca3af;
        }

        .afs-search-input:focus,
        .afs-date-select:focus,
        .afs-score-input:focus {
          border-color: #38bdf8;
          outline: 2px solid rgba(56, 189, 248, 0.15);
        }

        .afs-clear-search {
          position: absolute;
          top: 50%;
          right: 8px;
          transform: translateY(-50%);
          width: 32px;
          height: 32px;
          border: none;
          border-radius: 6px;
          background: transparent;
          color: #cbd5e1;
          font-size: 23px;
          cursor: pointer;
        }

        .afs-clear-search:hover {
          background: #374151;
        }

        .afs-date-select {
          display: block;
          width: 100%;
          min-height: 44px;
          margin-bottom: 12px;
          padding: 10px 12px;
          border: 1px solid #374151;
          border-radius: 8px;
          background: #1f2937;
          color: #fff;
          font-size: 15px;
        }

        .afs-results-count {
          margin: 10px 0 14px;
          color: #9ca3af;
          font-size: 13px;
        }

        .afs-match-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-height: 75vh;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 2px 4px 12px 0;
        }

        .afs-match-card {
          min-width: 0;
          padding: 15px;
          border: 1px solid #374151;
          border-radius: 10px;
          background: #1f2937;
          overflow-wrap: anywhere;
        }

        .afs-match-title {
          color: #fff;
          font-size: 16px;
          font-weight: 700;
          line-height: 1.5;
        }

        .afs-match-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 6px 12px;
          margin-top: 5px;
          color: #9ca3af;
          font-size: 13px;
        }

        .afs-score-line {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 6px;
          margin-top: 10px;
          font-size: 14px;
        }

        .afs-score-value {
          color: #fde047;
          font-weight: 700;
        }

        .afs-score-value.is-set {
          color: #86efac;
        }

        .afs-actions {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px;
          margin-top: 12px;
        }

        .afs-score-input {
          width: 130px;
          max-width: 100%;
          min-height: 40px;
          padding: 9px 10px;
          border: 1px solid #4b5563;
          border-radius: 7px;
          outline: none;
          background: #111827;
          color: #fff;
          font-size: 15px;
        }

        .afs-btn {
          min-height: 39px;
          padding: 9px 14px;
          border: 1px solid transparent;
          border-radius: 7px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
        }

        .afs-btn:disabled {
          cursor: not-allowed;
          opacity: 0.55;
        }

        .afs-btn-primary {
          background: #0284c7;
          color: #fff;
        }

        .afs-btn-primary:hover:not(:disabled) {
          background: #0369a1;
        }

        .afs-btn-edit {
          background: #065f46;
          color: #fff;
        }

        .afs-btn-edit:hover {
          background: #047857;
        }

        .afs-btn-cancel {
          border-color: #4b5563;
          background: #374151;
          color: #fff;
        }

        .afs-message {
          margin-top: 15px;
          padding: 12px;
          border: 1px solid #374151;
          border-radius: 8px;
          background: #1f2937;
          color: #fbbf24;
          font-size: 14px;
          overflow-wrap: anywhere;
        }

        .afs-empty {
          padding: 25px 15px;
          border: 1px dashed #4b5563;
          border-radius: 9px;
          color: #9ca3af;
          text-align: center;
        }

        @media (max-width: 600px) {
          .admin-final-score {
            padding: 12px;
          }

          .admin-final-score h2 {
            font-size: 19px;
          }

          .afs-match-card {
            padding: 12px;
          }

          .afs-score-input {
            width: 100%;
          }
        }
      `}</style>

      <h2>⚽ Admin: Add / Edit Final Score</h2>

      {/* LIVE SEARCH */}
      <div className="afs-search-area">
        <input
          className="afs-search-input"
          type="search"
          placeholder="🔎 Tafuta mechi kwa jina la timu..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          aria-label="Tafuta mechi"
          autoComplete="off"
        />

        {searchTerm && (
          <button
            type="button"
            className="afs-clear-search"
            onClick={() => setSearchTerm("")}
            aria-label="Futa search"
            title="Futa search"
          >
            ×
          </button>
        )}
      </div>

      {/* DATE FILTER */}
      <select
        className="afs-date-select"
        value={dateFilter}
        onChange={(event) => setDateFilter(event.target.value)}
        aria-label="Chagua tarehe za mechi"
      >
        <option value="today">Today</option>
        <option value="yesterday">Yesterday</option>
        <option value="thisWeek">This Week</option>
        <option value="lastWeek">Last Week</option>
        <option value="all">All Matches</option>
      </select>

      <div className="afs-results-count" aria-live="polite">
        {loading
          ? "Inapakia mechi..."
          : `${filtered.length} kati ya ${matches.length} mechi`}
      </div>

      {/* MATCH LIST */}
      <div className="afs-match-list">
        {loading ? (
          <div className="afs-empty">Inapakia mechi, subiri kidogo...</div>
        ) : filtered.length === 0 ? (
          <div className="afs-empty">
            {searchTerm
              ? `Hakuna mechi inayolingana na "${searchTerm}".`
              : "Hakuna mechi katika kipindi hiki."}
          </div>
        ) : (
          filtered.map((match) => {
            const teamA = getTeamA(match);
            const teamB = getTeamB(match);
            const date = getMatchDate(match);
            const time = getMatchTime(match);

            return (
              <div className="afs-match-card" key={match.id}>
                <div className="afs-match-title">
                  {teamA || "Timu A"} vs {teamB || "Timu B"}
                </div>

                <div className="afs-match-meta">
                  <span>📅 {date || "Tarehe haipo"}</span>
                  <span>🕒 {time || "Muda haupo"}</span>
                  {match.league && <span>🏆 {match.league}</span>}
                </div>

                <div className="afs-score-line">
                  <span>Final Score:</span>
                  <strong
                    className={`afs-score-value ${
                      match.finalScore ? "is-set" : ""
                    }`}
                  >
                    {match.finalScore || "— Not Set —"}
                  </strong>
                </div>

                {editingMatch === match.id ? (
                  <div className="afs-actions">
                    <input
                      className="afs-score-input"
                      type="text"
                      placeholder="Mfano: 3-1"
                      value={newScore}
                      onChange={(event) =>
                        setNewScore(event.target.value)
                      }
                      aria-label={`Final score ya ${teamA} dhidi ya ${teamB}`}
                      autoComplete="off"
                    />

                    <button
                      type="button"
                      className="afs-btn afs-btn-primary"
                      onClick={() => saveFinalScore(match)}
                      disabled={saving}
                    >
                      {saving ? "Inahifadhi..." : "Save Score"}
                    </button>

                    <button
                      type="button"
                      className="afs-btn afs-btn-cancel"
                      onClick={cancelEditing}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="afs-actions">
                    <button
                      type="button"
                      className="afs-btn afs-btn-edit"
                      onClick={() => {
                        setEditingMatch(match.id);
                        setNewScore(match.finalScore || "");
                      }}
                    >
                      {match.finalScore ? "Edit Score" : "Add Score"}
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {message && (
        <div className="afs-message" role="status" aria-live="polite">
          {message}
        </div>
      )}
    </div>
  );
}
